import React from "react";
import { useLocation } from "react-router-dom";
import {
  FaArrowRight,
  FaFacebookF,
  FaEnvelope,
  FaPhone,
  FaLocationDot,
  FaWhatsapp,
  FaInstagram,
  FaLinkedinIn,
  FaMoon,
  FaSun,
  FaXTwitter,
  FaXmark,
} from "react-icons/fa6";
import logo from "./assets/hallef-logo-cropped.png";
import tightLogo from "./assets/hallef-logo-tight.png";

const navItems = [
  { href: "/", label: "Home" },
  { href: "/about", label: "About" },
  { href: "/services", label: "Services" },
  { href: "/our-brands", label: "Our Brands" },
  { href: "/careers", label: "Careers" },
  { href: "/gallery", label: "Gallery" },
];

const footerItems = [
  { href: "/about", label: "About" },
  { href: "/services", label: "Services" },
  { href: "/our-brands", label: "Our Brands" },
  { href: "/careers", label: "Careers" },
  { href: "/gallery", label: "Gallery" },
  { href: "/inquiry", label: "Inquiry" },
];

const socialItems = [
  {
    href: "https://wa.me/918147796299",
    label: "WhatsApp",
    Icon: FaWhatsapp,
  },
  {
    href: "https://www.linkedin.com/in/nex-move-600063276/",
    label: "LinkedIn",
    Icon: FaLinkedinIn,
  },
  {
    href: "https://www.instagram.com/nexmove2024?igsh=NWczeWliN3R3N3Ji",
    label: "Instagram",
    Icon: FaInstagram,
  },
  {
    href: "https://www.facebook.com/Nexmove2019/",
    label: "Facebook",
    Icon: FaFacebookF,
  },
  // {
  //   href: "https://x.com/",
  //   label: "X",
  //   Icon: FaXTwitter,
  // },
];

export function isActivePath(pathname, href) {
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(`${href}/`);
}

/* =========================
   LOGO
========================= */
export function Logo({ tight = false }) {
  return (
    <a className="logo" href="/" aria-label="Hallef Technologies home">
      <img
        src={tight ? tightLogo : logo}
        alt="Hallef Technologies"
        className="logo-image"
      />
    </a>
  );
}

/* =========================
   HEADER
========================= */
export function SiteHeader() {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [isMobileHeader, setIsMobileHeader] = React.useState(() => window.innerWidth <= 991);

  React.useEffect(() => {
    const updateHeader = () => setIsMobileHeader(window.innerWidth <= 991);
    window.addEventListener("resize", updateHeader);
    return () => window.removeEventListener("resize", updateHeader);
  }, []);
  const [theme, setTheme] = React.useState(() => {
    if (typeof window === "undefined") return "light";
    return window.localStorage.getItem("hallef-theme") === "dark" ? "dark" : "light";
  });
  const { pathname } = useLocation();

  React.useEffect(() => {
    if (!menuOpen) return;
    const mobileQuery = window.matchMedia("(max-width: 991px)");
    if (!mobileQuery.matches) return;
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement;
    const navigation = document.getElementById("main-navigation");
    navigation?.querySelector("button")?.focus();
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
      if (event.key === "Tab") {
        const controls = navigation?.querySelectorAll("button, a[href]");
        if (!controls?.length) return;
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    const onResize = () => {
      if (!mobileQuery.matches) setMenuOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", onResize);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", onResize);
      previousFocus?.focus();
    };
  }, [menuOpen]);

  React.useEffect(() => {
    document.documentElement.dataset.theme = theme;
    window.localStorage.setItem("hallef-theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((currentTheme) => (currentTheme === "light" ? "dark" : "light"));
  };

  return (
    <header className="site-header">
      <div className="header-shell">

        <Logo tight />

        {menuOpen && <div className="mobile-menu-backdrop" onClick={() => setMenuOpen(false)} aria-hidden="true" />}

        <nav
          className={menuOpen ? "is-open" : ""}
          aria-label="Main navigation"
          id="main-navigation"
        >
          <button className="mobile-drawer-close menu-button" type="button" aria-label="Close navigation" onClick={() => setMenuOpen(false)}>
            <FaXmark aria-hidden="true" />
          </button>
          {navItems.map(({ href, label }) => {
            const active = isActivePath(pathname, href);

            return (
              <a
                key={href}
                href={href}
                className={active ? "is-active" : ""}
                aria-current={active ? "page" : undefined}
                onClick={() => setMenuOpen(false)}
              >
                {label}
              </a>
            );
          })}

          <a className="mobile-nav-link" href="/inquiry">
            Inquiry
          </a>
        </nav>

        <div className="header-actions">

          {!isMobileHeader && <a className="talk-button" href="/inquiry">
            Inquiry
            <FaArrowRight aria-hidden="true" />
          </a>}
          <button
            className={`theme-toggle unique-theme-toggle ${theme}`}
            type="button"
            aria-label={`Switch to ${theme === "light" ? "Dark" : "Light"} mode`}
            aria-pressed={theme === "dark"}
            onClick={toggleTheme}
          >
            <span className="theme-toggle-indicator" aria-hidden="true" />
            <span className="theme-toggle-option light-option">
              <FaSun aria-hidden="true" />
            </span>
            <span className="theme-toggle-option dark-option">
              <FaMoon aria-hidden="true" />
            </span>
          </button>
          <button
            className={`menu-button ${menuOpen ? "is-open" : ""}`}
            type="button"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-controls="main-navigation"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span />
            <span />
            <span />
          </button>

        </div>
      </div>
    </header>
  );
}

/* =========================
   FOOTER
========================= */
export function SiteFooter() {
  const { pathname } = useLocation();

  return (
    <footer className="site-footer">
      <div className="footer-inner">

        <div className="footer-main">

          <div className="footer-brand">

            <Logo tight />

            <p className="footer-tagline">
              Smarter technology for a better tomorrow.
            </p>

          </div>

          <div className="footer-column">

            <h3>Explore</h3>

            <nav
              className="footer-links"
              aria-label="Footer navigation"
            >
              {footerItems.map(({ href, label }) => {
                const active = isActivePath(pathname, href);

                return (
                  <a
                    key={href}
                    href={href}
                    className={active ? "is-active" : ""}
                    aria-current={active ? "page" : undefined}
                  >
                    {label}
                  </a>
                );
              })}
            </nav>

          </div>

          <div className="footer-contact">

            <h3>Contact</h3>

            <a className="footer-contact-link" href="mailto:info@halleftechnologies.com">
              <span className="footer-contact-icon"><FaEnvelope aria-hidden="true" /></span>
              <span>info@halleftechnologies.com</span>
            </a>

            <a className="footer-contact-link" href="tel:+918147796299">
              <span className="footer-contact-icon"><FaPhone aria-hidden="true" /></span>
              <span>+918147796299</span>
            </a>

            <p className="footer-address">
              <span className="footer-contact-icon"><FaLocationDot aria-hidden="true" /></span>
              <span>
              No 18, Buddha Vihara Road Cox Town,
              <br />
              Bangalore, Karnataka, India - 560005
              </span>
            </p>

            <div className="footer-socials">

              {socialItems.map(({ href, label, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  title={label}
                >
                  <Icon aria-hidden="true" />
                </a>
              ))}

            </div>

          </div>

        </div>

        <div className="footer-bottom">

          <p>
            &copy; {new Date().getFullYear()} Hallef Technologies. All rights
            reserved.
          </p>

          <nav className="footer-legal" aria-label="Legal links">
            <a href="/privacy">Privacy Policy</a>
            <span className="sep" aria-hidden="true">/</span>
            <a href="/terms">Terms &amp; Conditions</a>
          </nav>

        </div>

      </div>
    </footer>
  );
}
